import { initFederation } from '@angular-architects/native-federation';

// Some native-federation/es-module-shims dev paths can evaluate Angular code before
// ngDevMode is defined globally. Ensure it exists in dev/runtime bootstrap.
(globalThis as any).ngDevMode ??= true;

const MANIFEST_CACHE_KEY = 'etrm.module-config.runtime-config';

interface ModuleRuntimeConfig {
  nativeFederation: Record<string, string>;
  iframePages: Record<string, string>;
}

function hasManifestEntries(manifest: Record<string, string> | undefined): boolean {
  if (!manifest || typeof manifest !== 'object') {
    return false;
  }

  return Object.keys(manifest).some((moduleKey) => {
    const remoteEntry = manifest[moduleKey];
    return typeof remoteEntry === 'string' && remoteEntry.trim().length > 0;
  });
}

/**
 * Resolves the federation manifest as a plain object so:
 *  1. app.routes.ts can read it synchronously to build dynamic remote routes
 *  2. initFederation() receives an object (no extra HTTP fetch)
 *
 * Priority:
 *  - sessionStorage (written by subscription handler after an admin saves changes)
 *  - static federation.manifest.json served from S3/CloudFront
 */
async function resolveRuntimeConfig(): Promise<ModuleRuntimeConfig> {
  try {
    const raw = sessionStorage.getItem(MANIFEST_CACHE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') {
        if (parsed.nativeFederation || parsed.iframePages) {
          const runtimeConfigFromCache: ModuleRuntimeConfig = {
            nativeFederation: parsed.nativeFederation ?? {},
            iframePages: parsed.iframePages ?? {},
          };

          if (hasManifestEntries(runtimeConfigFromCache.nativeFederation) || hasManifestEntries(runtimeConfigFromCache.iframePages)) {
            return runtimeConfigFromCache;
          }

          // Ignore stale empty cache and continue to static fallback.
          sessionStorage.removeItem(MANIFEST_CACHE_KEY);
        } else {
          const legacyManifestFromCache = parsed as Record<string, string>;
          if (hasManifestEntries(legacyManifestFromCache)) {
            // Backward compatibility for legacy cache payload containing only manifest object.
            return {
              nativeFederation: legacyManifestFromCache,
              iframePages: {},
            };
          }

          // Ignore stale empty legacy cache and continue to static fallback.
          sessionStorage.removeItem(MANIFEST_CACHE_KEY);
        }
      }
    }
  } catch {
    sessionStorage.removeItem(MANIFEST_CACHE_KEY);
  }
  // Fetch and parse the static manifest
  try {
    const res = await fetch('federation.manifest.json');
    const nativeFederation = await res.json();
    return {
      nativeFederation,
      iframePages: {},
    };
  } catch {
    return {
      nativeFederation: {},
      iframePages: {},
    };
  }
}

resolveRuntimeConfig()
  .then(runtimeConfig => {
    const manifest = runtimeConfig.nativeFederation;
    // Expose to app.routes.ts before Angular bootstraps — routes are built synchronously
    (window as any)['__etrm_federation_manifest__'] = manifest;
    // Alias for easier ad-hoc debugging in browser console.
    (window as any)['etrm_federation_manifest'] = manifest;
    (window as any)['__etrm_iframe_pages__'] = runtimeConfig.iframePages;
    (window as any)['etrm_iframe_pages'] = runtimeConfig.iframePages;

    console.log('[federation.manifest.json] runtime manifest used:', manifest);
    console.log('[federation.manifest.json] runtime module keys:', Object.keys(manifest));
    console.log('[federation.manifest.json] iframe pages used:', runtimeConfig.iframePages);
    return initFederation(manifest);
  })
  .catch(err => console.error(err))
  .then(() => import('./bootstrap'))
  .catch(err => console.error(err));

